import { useUserStore } from '@/config/store/userStore'

export const MANAGEMENT_PAGES = [
  'ManagementAccounts',
  'ManagementAccount',
  'ManagementInvoice',
  'ManagementStats',
  'ManagementPricing',
  'ManagementBrochures',
  'ManagementServers',
  'ManagementCypressE2e',
  'ManagementAIHistory',
  'ManagementUserActivity',
  'ManagementSignatures',
  'ManagementAcademy',
  'AdminTicketOverview',
  'AdminTicketBoard',
  'AdminTicketList',
  'AdminTicketDetail',
] as const

export type ManagementPage = (typeof MANAGEMENT_PAGES)[number]

/** Preferred landing order when redirecting a restricted admin */
const MANAGEMENT_HOME_ORDER: ManagementPage[] = [
  'ManagementServers',
  'ManagementAccounts',
  'ManagementCypressE2e',
  'ManagementStats',
  'ManagementUserActivity',
  'ManagementSignatures',
  'ManagementPricing',
  'ManagementBrochures',
  'ManagementInvoice',
  'AdminTicketOverview',
  'AdminTicketBoard',
  'AdminTicketList',
  'ManagementAIHistory',
  'ManagementAcademy',
]

const getUserManagementPages = (user = useUserStore().user): string[] => {
  if (!user) return []
  if (Array.isArray(user.managementPages)) return user.managementPages
  if (Array.isArray(user.user?.managementPages)) return user.user.managementPages
  return []
}

/** Empty / missing managementPages = full access (legacy admin behavior). */
export const canAccessManagementPage = (
  page: string,
  user = useUserStore().user,
): boolean => {
  const pages = getUserManagementPages(user)
  if (!pages.length) return true
  return pages.includes(page)
}

export const getDefaultManagementHome = (user = useUserStore().user) => {
  const pages = getUserManagementPages(user)
  if (!pages.length) {
    return { name: 'ManagementServers' as const }
  }

  const firstAllowed = MANAGEMENT_HOME_ORDER.find((page) => pages.includes(page))
    || pages.find((page): page is ManagementPage =>
      (MANAGEMENT_PAGES as readonly string[]).includes(page),
    )

  if (firstAllowed) return { name: firstAllowed }

  return { name: 'ManagementServers' as const }
}

export const guardManagementPage = (page: ManagementPage) => () => {
  if (canAccessManagementPage(page)) return true
  return getDefaultManagementHome()
}
